// src/components/reports/preview/VacationReportPreview.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import VacationReportModel from '../../../models/VacationReportModel';

// ─────────────────────────────────────────────────────────────────────────────
// Vacation Overview - One row per vacation day entry, sorted by date then staff
// ─────────────────────────────────────────────────────────────────────────────

const VacationOverviewView = ({ data, isPreview, previewLimit }) => {
  const { t } = useTranslation('reports');

  // Sort by date ascending, then staff name alphabetically.
  // Mirrors the sort order applied in ReportService.loadVacationEntries.
  const sorted = [...data].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.staffName.localeCompare(b.staffName);
  });

  const displayData = isPreview ? sorted.slice(0, previewLimit) : sorted;

  return (
    <div className="report-preview vacation-overview-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="date-col">{t('date')}</th>
              <th className="staff-col">{t('staff')}</th>
            </tr>
          </thead>

          <tbody>
            {displayData.map((entry, index) => (
              <tr
                key={`${entry.staffId}-${entry.date}-${index}`}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{entry.getFormattedDate()}</td>
                <td>{entry.staffName}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td><strong>{t('total')}</strong></td>
              <td>
                <strong>
                  {data.length} {t('vacationDays')}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && data.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: data.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Vacation Summary - One aggregated row per staff member showing total days
// ─────────────────────────────────────────────────────────────────────────────

const VacationSummaryView = ({ data, isPreview, previewLimit }) => {
  const { t } = useTranslation('reports');

  // buildSummaryRows counts all entries as days and sorts by staff name.
  // Duration is not tracked in vacation reports.
  const summaryRows = VacationReportModel.buildSummaryRows(data);

  const displayRows = isPreview ? summaryRows.slice(0, previewLimit) : summaryRows;

  // Grand total computed from full summaryRows, not just displayed rows
  const grandTotalDays = summaryRows.reduce((sum, row) => sum + row.totalDays, 0);

  return (
    <div className="report-preview vacation-summary-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="staff-col">{t('staff')}</th>
              <th className="count-col">{t('vacationDays')}</th>
            </tr>
          </thead>

          <tbody>
            {displayRows.map((row, index) => (
              <tr
                key={row.staffId}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{row.staffName}</td>
                <td>{row.totalDays}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td><strong>{t('total')}</strong></td>
              <td><strong>{grandTotalDays}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && summaryRows.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: summaryRows.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Preview component for Vacation report types.
 * Handles both Vacation Overview (one row per vacation day entry) and
 * Vacation Summary (aggregated day count per staff member).
 *
 * Duration values are not displayed in vacation reports.
 * Each Firestore vacation document represents exactly one vacation day.
 *
 * Props are passed from ReportPreview.js - this component does not
 * read from context directly.
 *
 * @param {VacationReportModel[]} props.data - Array of VacationReportModel instances
 * @param {string} props.reportType - 'Vacation Overview' or 'Vacation Summary'
 * @param {boolean} props.isPreview - Whether to limit the displayed rows
 * @param {number} props.previewLimit - Maximum rows shown in preview mode
 */
const VacationReportPreview = ({
  data,
  reportType,
  isPreview = true,
  previewLimit = 10
}) => {
  if (reportType === 'Vacation Summary') {
    return (
      <VacationSummaryView
        data={data}
        isPreview={isPreview}
        previewLimit={previewLimit}
      />
    );
  }

  return (
    <VacationOverviewView
      data={data}
      isPreview={isPreview}
      previewLimit={previewLimit}
    />
  );
};

export default VacationReportPreview;